
import React, { useMemo, useState, useEffect } from 'react';
import WorkflowEditor from './components/editors/WorkflowEditor';
import WorkflowSimulator from './components/editors/WorkflowSimulator';
import { EditorProvider } from './contexts/EditorContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NODE_CATEGORIES } from './constants';
import { defaultComponentRegistry } from './utils/componentRegistry';
import { getNodeTypes, getPropertyRegistry, registerNodes, NodeRegistration } from './utils/nodeRegistry';
import { directoryService } from './services/mockDirectoryService';
import { DmnService, mockDmnFiles, DmnDefinition } from './services/mockDmnService';
import { useWorkflowEditor } from './hooks/useWorkflowEditor';
import { resolveTargetHandles } from './utils/resolveTargetHandles';
import { useWorkflowSimulation } from './hooks/useWorkflowSimulation';
import { ProcessDefinition, AIService, WorkflowProject } from './types';
import { AutoSavePayload } from './hooks/useAutoSave';

export interface ControllerProps {
  initialData?: ProcessDefinition[];
  onBack?: () => void;
  onSave?: (data: WorkflowProject) => Promise<void>;
  onSaveAndQuit?: (data: WorkflowProject) => Promise<void>;
  onAutoSave?: (data: AutoSavePayload) => void;
  isLoading?: boolean;
  customComponents?: Record<string, React.ComponentType<any>>;
  customNodes?: Record<string, NodeRegistration>;
  aiService?: AIService;
  directoryService?: any;
  dmnDefinitions?: DmnDefinition[];
  theme?: "light" | "dark";
}

const ControllerContent: React.FC<Omit<ControllerProps, 'theme'>> = ({
  initialData,
  onBack,
  onSave,
  onSaveAndQuit,
  onAutoSave,
  isLoading: externalLoading = false,
  customComponents = {},
  customNodes,
  aiService,
  directoryService: externalDirectoryService,
  dmnDefinitions
}) => {
  const [fallbackAi, setFallbackAi] = useState<AIService | null>(null);
  const [customNodesVersion, setCustomNodesVersion] = useState(0);

  useEffect(() => {
    if (customNodes) {
      registerNodes(customNodes);
      setCustomNodesVersion(v => v + 1);
    }
  }, [customNodes]);

  useEffect(() => {
    if (!aiService && !fallbackAi) {
      import('./services/geminiService').then(m => {
        setFallbackAi(m.geminiService);
      });
    }
  }, [aiService, fallbackAi]);

  const editor = useWorkflowEditor({
    initialData,
    onAutoSave
  });

  const resolvedEdges = useMemo(
    () => resolveTargetHandles(editor.nodes, editor.edges),
    [editor.nodes, editor.edges]
  );

  // Annotate each node with which of its target handles are actually in use
  // so CustomNode can hide the unused one. Stable-reference when unchanged
  // so React Flow doesn't churn.
  const decoratedNodes = useMemo(() => {
    const usage = new Map<string, { left: boolean; top: boolean }>();
    for (const edge of resolvedEdges) {
      if (edge.targetHandle !== 't-left' && edge.targetHandle !== 't-top') continue;
      const u = usage.get(edge.target) || { left: false, top: false };
      if (edge.targetHandle === 't-left') u.left = true;
      else u.top = true;
      usage.set(edge.target, u);
    }
    let changed = false;
    const next = editor.nodes.map((n) => {
      const u = usage.get(n.id) || { left: false, top: false };
      const prev = n.data?.targetHandleUsage;
      if (prev && prev.left === u.left && prev.top === u.top) return n;
      changed = true;
      return { ...n, data: { ...n.data, targetHandleUsage: u } };
    });
    return changed ? next : editor.nodes;
  }, [editor.nodes, resolvedEdges]);

  const simulator = useWorkflowSimulation({
      nodes: editor.nodes,
      setNodes: editor.setNodes,
      edges: editor.edges,
      fitView: editor.fitView,
      focusNode: editor.focusNode
  });

  // Initialize DMN Service with props or default
  const dmnServiceInstance = useMemo(() => {
      return new DmnService(dmnDefinitions || mockDmnFiles);
  }, [dmnDefinitions]);

  const config = useMemo(() => ({
    nodeTypes: getNodeTypes(),
    nodeCategories: NODE_CATEGORIES,
    propertyRegistry: getPropertyRegistry(),
    componentRegistry: { ...defaultComponentRegistry, ...customComponents },
    services: {
      ai: aiService || fallbackAi || {
          generateWorkflowCompletion: async () => "Initializing AI...",
          evaluateCondition: async () => true
      },
      directory: externalDirectoryService || directoryService,
      dmn: dmnServiceInstance
    },
    actions: {
        openModal: editor.openModalAtTab
    }
  }), [customComponents, customNodesVersion, editor.openModalAtTab, aiService, fallbackAi, dmnServiceInstance, externalDirectoryService]);

  const onNodeClick = simulator.simulationState.isRunning ? undefined : editor.onNodeClick;
  const onEdgeClick = simulator.simulationState.isRunning ? undefined : editor.onEdgeClick;
  const onPaneClick = simulator.simulationState.isRunning ? undefined : editor.onPaneClick;
  const onNodeDoubleClick = simulator.simulationState.isRunning ? undefined : editor.onNodeDoubleClick;
  const onNodeContextMenu = simulator.simulationState.isRunning ? undefined : editor.onNodeContextMenu;

  const handleExternalSave = async () => {
      if (onSave) {
          editor.setIsLoading(true);
          try {
              await onSave(editor.getProjectData());
          } finally {
              editor.setIsLoading(false);
          }
      }
  };

  const handleSaveAndQuitClick = async () => {
      if (onSaveAndQuit) {
          editor.setIsLoading(true);
          try {
              await onSaveAndQuit(editor.getProjectData());
          } finally {
              editor.setIsLoading(false);
          }
      }
  };

  const handleExport = (opts?: { bypassValidation?: boolean }) => {
      editor.handleSaveProject(opts);
  };

  const isGlobalLoading = editor.isLoading || externalLoading;

  return (
    <EditorProvider config={config}>
      {simulator.simulationState.isRunning ? (
        <WorkflowSimulator
          nodes={editor.nodes}
          edges={editor.edges}
          
          simulationState={simulator.simulationState}
          onToggleSimulation={simulator.stopSimulation}
          onResetSimulation={simulator.resetSimulation}
          onSimulationAction={simulator.handleSimulationAction}
          suggestedPath={simulator.suggestedPath}
          
          activeProcessId={editor.activeProcessId}
          processes={editor.processes}
          onSelectProcess={editor.handleSelectProcess}
          onCreateProcess={editor.handleCreateProcess}
          onDeleteProcess={editor.handleDeleteProcess}
          onSaveProject={handleExport}
          onLoadProject={editor.handleLoadProject}
          onBack={onBack}
          onSave={onSave ? handleExternalSave : undefined}
          onSaveAndQuit={onSaveAndQuit ? handleSaveAndQuitClick : undefined}
        />
      ) : (
        <WorkflowEditor
            nodes={decoratedNodes}
            edges={resolvedEdges}
            onNodesChange={editor.onNodesChange}
            onEdgesChange={editor.onEdgesChange}
            onConnect={editor.onConnect}
            onConnectStart={editor.onConnectStart}
            onConnectEnd={editor.onConnectEnd}
            onDrop={editor.onDrop}
            onDragOver={editor.onDragOver}
            
            simulationState={simulator.simulationState}
            onToggleSimulation={simulator.startSimulation}
            onResetSimulation={simulator.resetSimulation}
            
            activeProcessId={editor.activeProcessId}
            processes={editor.processes}
            onSelectProcess={editor.handleSelectProcess}
            onCreateProcess={editor.handleCreateProcess}
            onCreateSubProcess={editor.handleCreateSubProcess}
            onDeleteProcess={editor.handleDeleteProcess}
            onUpdateProcess={editor.handleUpdateProcess}
            
            onSaveProject={handleExport}
            onLoadProject={editor.handleLoadProject}
            onAutoLayout={editor.handleAutoLayout}
            
            onBack={onBack}
            onSave={onSave ? handleExternalSave : undefined}
            onSaveAndQuit={onSaveAndQuit ? handleSaveAndQuitClick : undefined}

            onNodeClick={onNodeClick!}
            onEdgeClick={onEdgeClick!}
            onNodeDoubleClick={onNodeDoubleClick!}
            onNodeContextMenu={onNodeContextMenu!}
            onNodeDragStop={editor.onNodeDragStop}
            onPaneClick={onPaneClick!}
            swimlanes={editor.swimlanes}
            selectedSwimlaneId={editor.selectedSwimlaneId}
            selectSwimlane={editor.selectSwimlane}
            addSwimlane={editor.addSwimlane}
            renameSwimlane={editor.renameSwimlane}
            moveSwimlane={editor.moveSwimlane}
            moveSwimlaneBy={editor.moveSwimlaneBy}
            resizeSwimlane={editor.resizeSwimlane}
            setSwimlaneColor={editor.setSwimlaneColor}
            removeSwimlane={editor.removeSwimlane}
            onSelectionChange={editor.onSelectionChange}
            selectedNode={editor.selectedNode}
            selectedEdge={editor.selectedEdge}
            updateNodeData={editor.updateNodeData}
            updateEdgeData={editor.updateEdgeData}
            onRenameNode={editor.renameNode}
            isModalOpen={editor.isModalOpen}
            setIsModalOpen={editor.setIsModalOpen}
            activeTabId={editor.activeTabId}
            setActiveTabId={editor.setActiveTabId}
            isDeployModalOpen={editor.isDeployModalOpen}
            setIsDeployModalOpen={editor.setIsDeployModalOpen}
            
            isLoading={isGlobalLoading}
            autoSaveState={editor.autoSaveState}
            variableContext={editor.variableContext}
            validationIssues={editor.validation.issues}
            validationErrors={editor.validation.errors}
            validationWarnings={editor.validation.warnings}
            processIssueCounts={editor.validation.processCounts}
            onFocusNode={editor.focusAndSelectNode}
        />
      )}
    </EditorProvider>
  );
};

const Controller: React.FC<ControllerProps> = ({ theme, ...rest }) => {
    return (
        <ThemeProvider forcedTheme={theme}>
            <ControllerContent {...rest} />
        </ThemeProvider>
    );
};

export default Controller;